import { useState } from 'react';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2 } from 'lucide-react';

import { useCMSContentTypesQuery, useCMSFieldDefsQuery } from '@/client/api/cms/cms.query';
import {
  useCreateFieldDefMutation,
  useDeleteFieldDefMutation,
  useUpdateFieldDefMutation,
} from '@/client/api/cms/cms.mutation';
import { CMS_FIELD_TYPES, CMSFieldDefinition, CMSFieldType } from '@/client/types/cms';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useListFilters } from '@/client/hooks/useListFilters';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const blank = {
  key: '',
  label: '',
  field_type: 'text' as CMSFieldType,
  ctype: '',
  required: false,
  sort_order: 10,
  options_json: '',
};

const FieldsAdminPage = () => {
  useBreadcrumbs([
    { label: 'Content', href: '#' },
    { label: 'Fields' },
  ]);

  // The content-type filter persists in the URL (?ctype=) across reloads.
  const { filters, handleFilterChange } = useListFilters<{ ctype: string }>({
    initialFilters: { ctype: '' },
  });
  const ctypeFilter = filters.ctype;
  const { data: types } = useCMSContentTypesQuery();
  const { data, isLoading } = useCMSFieldDefsQuery(ctypeFilter || undefined);
  const createMutation = useCreateFieldDefMutation();
  const updateMutation = useUpdateFieldDefMutation();
  const deleteMutation = useDeleteFieldDefMutation();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<CMSFieldDefinition | null>(null);
  const [form, setForm] = useState(blank);

  const fields = data ?? [];

  const openCreate = () => {
    setEditing(null);
    setForm({ ...blank, ctype: ctypeFilter });
    setOpen(true);
  };

  const openEdit = (f: CMSFieldDefinition) => {
    setEditing(f);
    setForm({
      key: f.key,
      label: f.label,
      field_type: (f.field_type as CMSFieldType) || 'text',
      ctype: f.ctype ?? '',
      required: !!f.required,
      sort_order: f.sort_order ?? 10,
      options_json: f.options_json != null ? JSON.stringify(f.options_json) : '',
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.key.trim() || !form.label.trim()) {
      toast.error('Key and label are required');
      return;
    }
    let options_json: unknown = null;
    if (form.options_json.trim()) {
      try {
        options_json = JSON.parse(form.options_json);
      } catch {
        toast.error('options_json must be valid JSON');
        return;
      }
    }
    const body = {
      key: form.key.trim(),
      label: form.label.trim(),
      field_type: form.field_type,
      ctype: form.ctype || null,
      required: form.required,
      sort_order: form.sort_order,
      options_json,
    };
    try {
      if (editing) {
        await updateMutation.mutateAsync({ cfdid: editing.cfdid, ...body });
        toast.success('Field updated');
      } else {
        await createMutation.mutateAsync(body);
        toast.success('Field created');
      }
      setOpen(false);
    } catch (e: any) {
      toast.error(e?.message ?? 'Save failed');
    }
  };

  const handleDelete = async (f: CMSFieldDefinition) => {
    if (!window.confirm(`Delete field "${f.label}"? Values will cascade.`)) return;
    try {
      await deleteMutation.mutateAsync(f.cfdid);
      toast.success('Deleted');
    } catch (e: any) {
      toast.error(e?.message ?? 'Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="mb-1 flex items-center gap-3">
            <ListChecks className="h-7 w-7 text-blue-700" />
            <h1 className="text-2xl font-bold text-gray-900">Custom fields</h1>
          </div>
          <p className="ml-10 text-sm text-gray-500">
            Field definitions for pages. Leave type empty for global fields.
          </p>
        </div>
        <Button onClick={openCreate} className="gap-2">
          <Plus className="h-4 w-4" />
          New field
        </Button>
      </div>

      <div className="mb-4">
        <select
          className="rounded-md border bg-white px-3 py-2 text-sm"
          value={ctypeFilter}
          onChange={(e) => handleFilterChange('ctype', e.target.value)}
        >
          <option value="">All types (+ global)</option>
          {(types ?? []).map((c) => (
            <option key={c.key} value={c.key}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Key', 'Label', 'Type', 'Scope', 'Required', ''].map((h) => (
                <th key={h || 'a'} className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading &&
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((__, j) => (
                    <td key={j} className="px-6 py-4">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))}
            {!isLoading && fields.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-16 text-center text-sm text-gray-500">
                  No field definitions yet.
                </td>
              </tr>
            )}
            {!isLoading &&
              fields.map((f) => (
                <tr key={f.cfdid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 font-mono text-sm">{f.key}</td>
                  <td className="px-6 py-4">
                    <button className="text-sm font-medium hover:text-blue-700" onClick={() => openEdit(f)}>
                      {f.label}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-xs">{f.field_type}</td>
                  <td className="px-6 py-4 text-xs text-gray-600">{f.ctype ?? 'global'}</td>
                  <td className="px-6 py-4 text-xs">{f.required ? 'Yes' : '—'}</td>
                  <td className="px-6 py-4 text-right">
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(f)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit field' : 'New field'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Key</Label>
              <Input
                value={form.key}
                disabled={!!editing}
                onChange={(e) => setForm((p) => ({ ...p, key: e.target.value.replace(/\s+/g, '_').toLowerCase() }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Label</Label>
              <Input value={form.label} onChange={(e) => setForm((p) => ({ ...p, label: e.target.value }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Field type</Label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={form.field_type}
                onChange={(e) => setForm((p) => ({ ...p, field_type: e.target.value as CMSFieldType }))}
              >
                {CMS_FIELD_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>Content type (blank = global)</Label>
              <select
                className="w-full rounded-md border px-3 py-2 text-sm"
                value={form.ctype}
                onChange={(e) => setForm((p) => ({ ...p, ctype: e.target.value }))}
              >
                <option value="">Global</option>
                {(types ?? []).map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.required}
                onChange={(e) => setForm((p) => ({ ...p, required: e.target.checked }))}
              />
              Required
            </label>
            <div className="space-y-1.5">
              <Label>Options JSON (select / multiselect)</Label>
              <Input
                placeholder='["primary","ghost"]'
                value={form.options_json}
                onChange={(e) => setForm((p) => ({ ...p, options_json: e.target.value }))}
              />
            </div>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FieldsAdminPage;
